import type { ApiErrorCode, ConnectionInput, DialectId, SslMode } from '@bits2schema/shared';
import { ApiError } from '../api/client';
import type { MessageKey, TranslateFn } from '../i18n/types';

const ERROR_KEYS: Record<ApiErrorCode, MessageKey> = {
  validationFailed: 'error.validationFailed',
  notFound: 'error.notFound',
  connectionFailed: 'error.connectionFailed',
  importFailed: 'error.importFailed',
  syncFailed: 'error.syncFailed',
  unsupportedDialect: 'error.unsupportedDialect',
  fileNotFound: 'error.fileNotFound',
  pathNotAllowed: 'error.pathNotAllowed',
  notImplemented: 'error.notImplemented',
  internalError: 'error.internalError',
  // Epic 14 — uživatelé a oprávnění.
  unauthorized: 'error.unauthorized',
  invalidCredentials: 'error.invalidCredentials',
  forbidden: 'error.forbidden',
  usernameTaken: 'error.usernameTaken',
  editLeaseHeld: 'error.editLeaseHeld',
};

/**
 * Lokalizovaný popis chyby + původní technický detail. Hláška z DB driveru
 * se nepřekládá (pozadavky.md kap. 6) — připojuje se v původním znění.
 */
export function describeError(error: unknown, t: TranslateFn): string {
  if (error instanceof ApiError) {
    const label = t(ERROR_KEYS[error.code] ?? 'error.internalError');
    return error.message ? `${label} ${error.message}` : label;
  }
  if (error instanceof Error && error.message) return error.message;
  return t('common.unknownError');
}

export function formatDateTime(value: string | null, t: TranslateFn, language: string): string {
  if (!value) return t('common.never');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = size / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export interface ConnectionDraft {
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  sslMode: SslMode;
  filePath: string;
}

export function emptyConnectionDraft(): ConnectionDraft {
  return {
    host: 'localhost',
    port: '5432',
    database: '',
    user: '',
    password: '',
    sslMode: 'disable',
    filePath: '',
  };
}

/** Převod formulářového stavu na vstup API podle zvoleného dialektu. */
export function toConnectionInput(dialect: DialectId, draft: ConnectionDraft): ConnectionInput {
  if (dialect === 'duckdb') {
    return { kind: 'duckdb', filePath: draft.filePath.trim() };
  }
  return {
    kind: 'postgresql',
    host: draft.host.trim(),
    port: Number.parseInt(draft.port, 10) || 0,
    database: draft.database.trim(),
    user: draft.user.trim(),
    password: draft.password,
    sslMode: draft.sslMode,
  };
}

/** Je formulář vyplněný natolik, aby mělo smysl zkoušet připojení? */
export function isConnectionDraftComplete(dialect: DialectId, draft: ConnectionDraft): boolean {
  if (dialect === 'duckdb') return draft.filePath.trim().length > 0;
  const port = Number.parseInt(draft.port, 10);
  return (
    draft.host.trim().length > 0 &&
    Number.isFinite(port) &&
    port > 0 &&
    port <= 65535 &&
    draft.database.trim().length > 0 &&
    draft.user.trim().length > 0
  );
}
